import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ClientPortalService, { ClientPortalUser as ServiceClientPortalUser, LoginActivity as ServiceLoginActivity, PortalStats } from '../../../services/ClientPortalService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  Divider,
  Menu,
  MenuItem as MenuItemComponent,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Security as SecurityIcon,
  Key as KeyIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Login as LoginIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useNotify } from '../../ui/NotificationSystem';

interface ClientPortalUser {
  id: string;
  clientId: number;
  clientName: string;
  name: string;
  email: string;
  role: string;
  accessLevel: 'basic' | 'standard' | 'advanced';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  lastLogin?: string;
  loginCount: number;
  createdAt: string;
  permissions: {
    viewProjects: boolean;
    viewBilling: boolean;
    downloadFiles: boolean;
    createTickets: boolean;
    viewReports: boolean;
    manageContacts: boolean;
  };
  notifications: {
    projectUpdates: boolean;
    billing: boolean;
    security: boolean;
    marketing: boolean;
  };
  temporaryPassword?: string;
  passwordExpiry?: string;
}

interface LoginActivity {
  id: string;
  userId: string;
  userName: string;
  action: 'login' | 'logout' | 'failed_login' | 'password_reset' | 'access_denied';
  ipAddress: string;
  userAgent: string;
  timestamp: string;
  success: boolean;
  details?: string;
}

const MOCK_PORTAL_USERS_FALLBACK: ClientPortalUser[] = [
  {
    id: '1',
    clientId: 1,
    clientName: 'TechCorp Inc',
    name: 'John Smith',
    email: 'john.smith@techcorp.com',
    role: 'CTO',
    accessLevel: 'advanced',
    status: 'active',
    lastLogin: '2024-10-15T14:30:00Z',
    loginCount: 45,
    createdAt: '2024-01-15T10:00:00Z',
    permissions: {
      viewProjects: true,
      viewBilling: true,
      downloadFiles: true,
      createTickets: true,
      viewReports: true,
      manageContacts: true
    },
    notifications: {
      projectUpdates: true,
      billing: true,
      security: true,
      marketing: false
    }
  },
  {
    id: '2',
    clientId: 1,
    clientName: 'TechCorp Inc',
    name: 'Jane Doe',
    email: 'jane.doe@techcorp.com',
    role: 'Project Manager',
    accessLevel: 'standard',
    status: 'active',
    lastLogin: '2024-10-14T09:15:00Z',
    loginCount: 28,
    createdAt: '2024-02-01T11:00:00Z',
    permissions: {
      viewProjects: true,
      viewBilling: false,
      downloadFiles: true,
      createTickets: true,
      viewReports: true,
      manageContacts: false
    },
    notifications: {
      projectUpdates: true,
      billing: false,
      security: true,
      marketing: false
    }
  },
  {
    id: '3',
    clientId: 2,
    clientName: 'StartupXYZ',
    name: 'Sarah Johnson',
    email: 'sarah@startupxyz.com',
    role: 'CEO',
    accessLevel: 'advanced',
    status: 'pending',
    loginCount: 0,
    createdAt: '2024-10-10T16:00:00Z',
    permissions: {
      viewProjects: true,
      viewBilling: true,
      downloadFiles: true,
      createTickets: true,
      viewReports: true,
      manageContacts: true
    },
    notifications: {
      projectUpdates: true,
      billing: true,
      security: true,
      marketing: true
    },
    temporaryPassword: 'TempPass123!',
    passwordExpiry: '2024-10-25T16:00:00Z'
  }
];

const MOCK_LOGIN_ACTIVITY_FALLBACK: LoginActivity[] = [
  {
    id: '1',
    userId: '1',
    userName: 'John Smith',
    action: 'login',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    timestamp: '2024-10-15T14:30:00Z',
    success: true
  },
  {
    id: '2',
    userId: '2',
    userName: 'Jane Doe',
    action: 'failed_login',
    ipAddress: '192.168.1.101',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
    timestamp: '2024-10-14T09:10:00Z',
    success: false,
    details: 'Invalid password'
  },
  {
    id: '3',
    userId: '2',
    userName: 'Jane Doe',
    action: 'login',
    ipAddress: '192.168.1.101',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
    timestamp: '2024-10-14T09:15:00Z',
    success: true
  }
];

const ClientPortalAccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<ClientPortalUser[]>([]);
  const [loginActivity, setLoginActivity] = useState<LoginActivity[]>([]);
  const [stats, setStats] = useState<PortalStats | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [userDialog, setUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<ClientPortalUser | null>(null);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ClientPortalUser | null>(null);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<ClientPortalUser | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<Partial<ClientPortalUser>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const { success: notifySuccess, error: notifyError } = useNotify();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, activityData, statsData] = await Promise.all([
        ClientPortalService.getPortalUsers(),
        ClientPortalService.getLoginActivity({ limit: 100 }),
        ClientPortalService.getPortalStats()
      ]);
      
      setUsers(usersData);
      setLoginActivity(activityData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading portal data:', error);
      notifyError('Failed to load portal data. Using fallback data.');
      // Use fallback data if API fails
      setUsers(MOCK_PORTAL_USERS_FALLBACK);
      setLoginActivity(MOCK_LOGIN_ACTIVITY_FALLBACK);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      accessLevel: 'standard',
      status: 'pending',
      permissions: {
        viewProjects: true,
        viewBilling: false,
        downloadFiles: true,
        createTickets: true,
        viewReports: false,
        manageContacts: false
      },
      notifications: {
        projectUpdates: true,
        billing: false,
        security: true,
        marketing: false
      }
    });
    setUserDialog(true);
  };

  const handleEditUser = (user: ClientPortalUser) => {
    setEditingUser(user);
    setFormData(user);
    setUserDialog(true);
    handleMenuClose();
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        // Update existing user
        const updatedUser = await ClientPortalService.updatePortalUser(editingUser.id, formData);
        setUsers(prev => prev.map(u => 
          u.id === editingUser.id ? updatedUser : u
        ));
        notifySuccess('User updated successfully');
      } else {
        // Create new user
        const newUser = await ClientPortalService.createPortalUser(formData);
        setUsers(prev => [...prev, newUser]);
        notifySuccess('User created successfully');
      }
      setUserDialog(false);
      await loadData(); // Refresh data
    } catch (error) {
      console.error('Error saving user:', error);
      notifyError('Failed to save user');
    }
  };

  const handleResetPassword = async (user: ClientPortalUser) => {
    setSelectedUser(user);
    try {
      const result = await ClientPortalService.resetPassword(user.id);
      setNewPassword(result.temporaryPassword);
      setPasswordDialog(true);
      
      setUsers(prev => prev.map(u => 
        u.id === user.id 
          ? { ...u, temporaryPassword: result.temporaryPassword, passwordExpiry: result.expiresAt }
          : u
      ));
    } catch (error) {
      console.error('Error resetting password:', error);
      notifyError('Failed to reset password');
    }
    handleMenuClose();
  };

  const handleSendCredentials = async (user: ClientPortalUser) => {
    try {
      await ClientPortalService.sendCredentials(user.id);
      notifySuccess(`Login credentials sent to ${user.email}`);
    } catch (error) {
      console.error('Error sending credentials:', error);
      notifyError('Failed to send credentials');
    }
    handleMenuClose();
  };

  const handleToggleStatus = async (user: ClientPortalUser) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      const updatedUser = await ClientPortalService.toggleStatus(user.id, newStatus);
      setUsers(prev => prev.map(u => 
        u.id === user.id ? updatedUser : u
      ));
      notifySuccess(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error toggling user status:', error);
      notifyError('Failed to update user status');
    }
    handleMenuClose();
  };

  const handleDeleteUser = async (user: ClientPortalUser) => {
    if (window.confirm(`Are you sure you want to delete ${user.name}?`)) {
      try {
        await ClientPortalService.deletePortalUser(user.id);
        setUsers(prev => prev.filter(u => u.id !== user.id));
        notifySuccess('User deleted successfully');
        await loadData();
      } catch (error) {
        console.error('Error deleting user:', error);
        notifyError('Failed to delete user');
      }
    }
    handleMenuClose();
  };

  const generatePassword = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: ClientPortalUser) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const getStatusColor = (status: ClientPortalUser['status']) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: ClientPortalUser['status']) => {
    switch (status) {
      case 'active': return <CheckCircleIcon />;
      case 'inactive': return <BlockIcon />;
      case 'suspended': return <WarningIcon />;
      case 'pending': return <HistoryIcon />;
      default: return <CheckCircleIcon />;
    }
  };

  const getActionIcon = (action: LoginActivity['action']) => {
    switch (action) {
      case 'login': return <LoginIcon color="success" />;
      case 'logout': return <LoginIcon color="action" />;
      case 'failed_login': return <WarningIcon color="error" />;
      case 'password_reset': return <KeyIcon color="warning" />;
      case 'access_denied': return <BlockIcon color="error" />;
      default: return <HistoryIcon />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/dashboard/admin/clients')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4">
          Client Portal Access Management
        </Typography>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, 
          gap: 2, 
          mb: 3 
        }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.totalUsers}</Typography>
              <Typography variant="body2" color="text.secondary">Total Users</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.activeUsers}</Typography>
              <Typography variant="body2" color="text.secondary">Active</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{stats.pendingUsers}</Typography>
              <Typography variant="body2" color="text.secondary">Pending</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">{stats.totalLogins}</Typography>
              <Typography variant="body2" color="text.secondary">Total Logins</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">{stats.failedAttempts}</Typography>
              <Typography variant="body2" color="text.secondary">Failed Attempts</Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Main Content Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Portal Users" icon={<SecurityIcon />} />
            <Tab label="Login Activity" icon={<HistoryIcon />} />
          </Tabs>
        </Box>

        <CardContent>
          {/* Portal Users Tab */}
          {activeTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Portal Users</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateUser}
                >
                  Add Portal User
                </Button>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Access Level</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell>Login Count</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">{user.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {user.email}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.role}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{user.clientName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={user.accessLevel} 
                            size="small"
                            color={user.accessLevel === 'advanced' ? 'primary' : user.accessLevel === 'standard' ? 'info' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(user.status)}
                            label={user.status}
                            color={getStatusColor(user.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {user.lastLogin ? (
                            new Date(user.lastLogin).toLocaleDateString()
                          ) : (
                            <Typography variant="body2" color="text.secondary">Never</Typography>
                          )}
                        </TableCell>
                        <TableCell>{user.loginCount}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, user)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Login Activity Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>Recent Login Activity</Typography>
              
              <List>
                {loginActivity.map((activity) => (
                  <ListItem key={activity.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                    <ListItemIcon>
                      {getActionIcon(activity.action)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2">
                            {activity.userName} - {activity.action.replace('_', ' ')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(activity.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            IP: {activity.ipAddress}
                          </Typography>
                          {activity.details && (
                            <Typography variant="body2" color="error.main">
                              {activity.details}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItemComponent onClick={() => menuUser && handleEditUser(menuUser)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit User
        </MenuItemComponent>
        <MenuItemComponent onClick={() => menuUser && handleResetPassword(menuUser)}>
          <KeyIcon sx={{ mr: 1 }} fontSize="small" />
          Reset Password
        </MenuItemComponent>
        <MenuItemComponent onClick={() => menuUser && handleSendCredentials(menuUser)}>
          <SendIcon sx={{ mr: 1 }} fontSize="small" />
          Send Credentials
        </MenuItemComponent>
        <MenuItemComponent onClick={() => menuUser && handleToggleStatus(menuUser)}>
          {menuUser?.status === 'active' ? <BlockIcon sx={{ mr: 1 }} fontSize="small" /> : <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" />}
          {menuUser?.status === 'active' ? 'Deactivate' : 'Activate'}
        </MenuItemComponent>
        <Divider />
        <MenuItemComponent onClick={() => menuUser && handleDeleteUser(menuUser)}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete User
        </MenuItemComponent>
      </Menu>

      {/* User Create/Edit Dialog */}
      <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit Portal User' : 'Create Portal User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Role"
                value={formData.role || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Access Level</InputLabel>
                <Select
                  value={formData.accessLevel || 'standard'}
                  onChange={(e) => setFormData(prev => ({ ...prev, accessLevel: e.target.value as any }))}
                  label="Access Level"
                >
                  <MenuItem value="basic">Basic - View only</MenuItem>
                  <MenuItem value="standard">Standard - View and comment</MenuItem>
                  <MenuItem value="advanced">Advanced - Full access</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom>Permissions</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(formData.permissions || {}).map(([key, value]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        checked={!!value}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          permissions: {
                            ...prev.permissions!,
                            [key]: e.target.checked
                          }
                        }))}
                      />
                    }
                    label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {editingUser ? 'Update User' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will generate a new temporary password that expires in 7 days.
          </Alert>
          
          <TextField
            fullWidth
            label="New Temporary Password"
            value={newPassword}
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOffIcon /> : <ViewIcon />}
                </IconButton>
              )
            }}
            sx={{ mb: 2 }}
          />
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => setNewPassword(generatePassword())}
            sx={{ mb: 2 }}
          >
            Generate New Password
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              // TODO: Implement password reset
              notifySuccess('Password reset successfully');
              setPasswordDialog(false);
            }} 
            variant="contained"
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientPortalAccessPage;
